import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './BlogsAdmin.css';
import { blogPosts as initialPosts } from './blogData';

const ADMIN_USERNAME = 'ccuts';
const ADMIN_PASSWORD = '123456';

const emptyPost = {
  id: null,
  title: '',
  slug: '',
  date: '',
  category: '',
  image: '',
  description: '',
  descriptionWithLink: '',
  descriptionAfterLink: '',
  postUrl: 'https://celebritycuts.in/',
  content: ''
};

const generateSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const BlogsAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [credentials, setCredentials] = useState({ id: '', password: '' });
  const [posts, setPosts] = useState(() => {
    const saved = window.localStorage.getItem('blogsAdminPosts');
    return saved ? JSON.parse(saved) : initialPosts.map(post => ({ ...post }));
  });
  const [form, setForm] = useState(emptyPost);
  const [editingId, setEditingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const contentRef = useRef(null);

  const handleLogin = (event) => {
    event.preventDefault();
    const { id, password } = credentials;

    if (id.trim() === ADMIN_USERNAME && password.trim() === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setLoginError('');
      setStatusMessage('Logged in successfully. You can now add, edit, or delete blog posts.');
      return;
    }

    setLoginError('Invalid username or password. Use id: ccuts and password: 123456.');
    setStatusMessage('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({ ...prev, image: reader.result || '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleEditPost = (post) => {
    setEditingId(post.id);
    setForm({ ...post });
    setStatusMessage('Editing the selected blog post. Update the form and click Save.');
  };

  const insertContentText = (insertedText, selectionOnly = false) => {
    const textarea = contentRef.current;
    if (!textarea) {
      setForm(prev => ({ ...prev, content: prev.content + insertedText }));
      return;
    }

    const { selectionStart, selectionEnd, value } = textarea;
    const selected = value.slice(selectionStart, selectionEnd);
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const newText = selectionOnly && selected ? selected + insertedText : insertedText;
    const finalText = before + newText + after;

    setForm(prev => ({ ...prev, content: finalText }));
    setTimeout(() => {
      const cursor = before.length + newText.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const applyContentFormat = (formatType) => {
    const textarea = contentRef.current;
    const value = textarea ? textarea.value : form.content;
    const selectionStart = textarea ? textarea.selectionStart : value.length;
    const selectionEnd = textarea ? textarea.selectionEnd : value.length;
    const selected = value.slice(selectionStart, selectionEnd) || '';

    let formatted = '';
    switch (formatType) {
      case 'bold':
        formatted = selected ? `**${selected}**` : '**bold text**';
        break;
      case 'italic':
        formatted = selected ? `*${selected}*` : '*italic text*';
        break;
      case 'underline':
        formatted = selected ? `__${selected}__` : '__underlined text__';
        break;
      case 'link':
        formatted = selected ? `[${selected}](https://example.com)` : '[Link text](https://example.com)';
        break;
      case 'bullet':
        if (selected) {
          formatted = selected.split('\n').map(line => line.startsWith('- ') ? line : `- ${line}`).join('\n');
        } else {
          formatted = '- New bullet item';
        }
        break;
      case 'number':
        if (selected) {
          formatted = selected.split('\n').map((line, index) => `${index + 1}. ${line.replace(/^\d+\.\s*/, '')}`).join('\n');
        } else {
          formatted = '1. First item';
        }
        break;
      case 'quote':
        if (selected) {
          formatted = selected.split('\n').map(line => `> ${line}`).join('\n');
        } else {
          formatted = '> Quote text';
        }
        break;
      case 'code':
        formatted = selected ? `\`${selected}\`` : '`code snippet`';
        break;
      case 'image':
        formatted = selected ? `![${selected}](https://example.com/image.jpg)` : '![Alt text](https://example.com/image.jpg)';
        break;
      default:
        formatted = selected;
    }

    if (textarea) {
      const before = value.slice(0, selectionStart);
      const after = value.slice(selectionEnd);
      const finalText = before + formatted + after;
      setForm(prev => ({ ...prev, content: finalText }));
      setTimeout(() => {
        const cursor = before.length + formatted.length;
        textarea.focus();
        textarea.setSelectionRange(cursor, cursor);
      }, 0);
    } else {
      setForm(prev => ({ ...prev, content: prev.content + formatted }));
    }
  };

  const handleDeletePost = (postId) => {
    const deletedPosts = posts.filter(post => post.id !== postId);
    setPosts(deletedPosts);
    if (editingId === postId) {
      setEditingId(null);
      setForm(emptyPost);
    }
    setStatusMessage('Blog post deleted successfully.');
  };

  useEffect(() => {
    window.localStorage.setItem('blogsAdminPosts', JSON.stringify(posts));
  }, [posts]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyPost);
    setStatusMessage('Form cleared. Enter new blog details to upload a fresh post.');
  };

  const handleSavePost = (event) => {
    event.preventDefault();
    if (!form.title || !form.date) {
      setStatusMessage('Please fill in at least Title and Date.');
      return;
    }

    const updatedPost = {
      ...form,
      id: editingId || Date.now(),
      slug: form.slug ? form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-').replace(/^-|-$/g, '') : generateSlug(form.title),
      category: form.category || 'Blog'
    };

    if (editingId) {
      setPosts(posts.map(post => (post.id === editingId ? updatedPost : post)));
      setStatusMessage('Blog post updated successfully.');
    } else {
      setPosts([updatedPost, ...posts]);
      setStatusMessage('New blog post uploaded successfully.');
    }

    setEditingId(null);
    setForm(emptyPost);
  };

  const renderContentPreview = () => {
    if (!form.content) return null;
    const lines = form.content.split('\n').filter(Boolean);
    return (
      <div className="admin-preview-block">
        <h3>Preview</h3>
        {lines.length > 1 ? (
          <ul>
            {lines.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        ) : (
          <p>{lines[0]}</p>
        )}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="blogs-admin-login-page">
        <div className="blogs-admin-card">
          <h1>Celebrity Cuts Blog Admin</h1>
          <p>Enter login credentials to manage blog posts.</p>
          <form onSubmit={handleLogin} className="blogs-admin-form">
            <label>
              Username
              <input
                name="id"
                value={credentials.id}
                onChange={handleInputChange}
                placeholder="ccuts"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder="123456"
              />
            </label>
            {loginError && <div className="blogs-admin-error">{loginError}</div>}
            <button type="submit" className="blogs-admin-button">Login</button>
          </form>
          <div className="blogs-admin-hint">
            Use <strong>ccuts</strong> / <strong>123456</strong>
          </div>
          <Link to="/blog" className="blogs-admin-link">Back to public blog page</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blogs-admin-page">
      <div className="blogs-admin-header">
        <h1>Blog Manager</h1>
        <p>Upload, edit, and delete blog posts in this admin tool.</p>
        <div className="blogs-admin-credentials">
          <span><strong>Admin:</strong> ccuts</span>
          <span><strong>Password:</strong> 123456</span>
        </div>
      </div>

      <div className="blogs-admin-content">
        <section className="blogs-admin-form-section">
          <div className="blogs-admin-form-title-group">
            <div className="blogs-admin-form-badge">Upload New Blog Post</div>
            <h2>{editingId ? 'Edit Blog Post' : 'Upload New Blog Post'}</h2>
            <p className="blogs-admin-form-subtitle">Use the form below to add a new post or update an existing one. The table of posts appears below for edit and delete actions.</p>
          </div>
          <div className="blogs-admin-form-panel">
            <form onSubmit={handleSavePost} className="blogs-admin-form">
              <div className="blogs-admin-form-grid">
                <label>
                  Title
                  <input name="title" value={form.title} onChange={handleFormChange} placeholder="Blog title" />
                </label>
                <label>
                  Date
                  <input name="date" value={form.date} onChange={handleFormChange} placeholder="March 21, 2024" />
                </label>
                <label className="full-width-field">
                  Blog image upload
                  <input type="file" accept="image/*" onChange={handleImageUpload} />
                </label>
              </div>
              <label>
                Intro description
                <textarea name="description" value={form.description} onChange={handleFormChange} rows="3" placeholder="Intro text before the link" />
              </label>
              <label>
                Link text
                <input name="descriptionWithLink" value={form.descriptionWithLink} onChange={handleFormChange} placeholder="best hair salon in Visakhapatnam" />
              </label>
              <label>
                Link text after link
                <textarea name="descriptionAfterLink" value={form.descriptionAfterLink} onChange={handleFormChange} rows="2" placeholder="can make all the difference" />
              </label>
              <label>
                Link URL
                <input name="postUrl" value={form.postUrl} onChange={handleFormChange} placeholder="https://celebritycuts.in/" />
              </label>
              <label>
                Blog content / bullet points
                <div className="editor-toolbar">
                  <button type="button" onClick={() => applyContentFormat('bold')}>B</button>
                  <button type="button" onClick={() => applyContentFormat('italic')}>I</button>
                  <button type="button" onClick={() => applyContentFormat('underline')}>U</button>
                  <button type="button" onClick={() => applyContentFormat('link')}>Link</button>
                  <button type="button" onClick={() => applyContentFormat('bullet')}>•</button>
                  <button type="button" onClick={() => applyContentFormat('number')}>1.</button>
                  <button type="button" onClick={() => applyContentFormat('quote')}>"</button>
                  <button type="button" onClick={() => applyContentFormat('code')}>Code</button>
                  <button type="button" onClick={() => applyContentFormat('image')}>Img</button>
                </div>
                <textarea
                  name="content"
                  ref={contentRef}
                  value={form.content}
                  onChange={handleFormChange}
                  rows="8"
                  className="editor-textarea"
                  placeholder="Enter one item per line for bullets"
                />
              </label>
              {form.image && (
                <div className="blogs-admin-image-preview">
                  <img src={form.image} alt="Blog preview" />
                </div>
              )}
              <div className="blogs-admin-form-actions">
                <button type="submit">Save Post</button>
                <button type="button" onClick={resetForm} className="secondary-button">Clear</button>
              </div>
            </form>
            {renderContentPreview()}
            {statusMessage && <div className="blogs-admin-status">{statusMessage}</div>}
          </div>
        </section>

        <section className="blogs-admin-table-section">
          <h2>All Blog Posts</h2>
          <table className="blogs-admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Date</th>
                <th>Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id}>
                  <td>{post.title}</td>
                  <td>{post.slug}</td>
                  <td>{post.date}</td>
                  <td>{post.category}</td>
                  <td className="blogs-admin-actions">
                    <button type="button" onClick={() => handleEditPost(post)}>Edit</button>
                    <button type="button" onClick={() => handleDeletePost(post.id)} className="delete-button">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="blogs-admin-footer">
        <Link to="/blog" className="blogs-admin-link">View public blog page</Link>
      </div>
    </div>
  );
};

export default BlogsAdmin;
